import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap, switchMap, filter } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { AppSettings } from '../app.settings';
import { ToastrService } from 'ngx-toastr';
import * as dayjs from 'dayjs';

import { CacheModel } from '../models/cache.model';
import { Response } from '../models/response.model';
import { Order } from '../models/order.model';
import { OrderItem } from '../models/order-item.model';
import { OrderWindow } from '../models/order-window.model';
import { CostCenter } from '../models/cost-center.model';
import { OrderTypes } from '../enums/order-types';
import { ProductService } from './product.service';
import { AuthService } from './auth.service';
import { EntityService } from './entity.service';
import { CreditCard } from '../models/credit-card.model';
import { ApiOrderService } from './api-order.service';
import { QUOTE_STATE } from '../constants/order-states';
import { Router } from '@angular/router';
import { GoogleAnalytics } from './google-analytics.service';
import {
  ACTIVE_ORDER_WINDOW_ID,
  LocalStorageService,
} from './local-storage.service';
import { isWSLROrder } from '../shared/helpers';
import { isEmptyArray } from '../shared/utils';
import { BudgetService } from './budget.service';
import { Error } from '../interfaces/error';
import { ErrorCodes } from '../enums/error.codes';
import { OrderHistory } from '../models/order-history.model';
import { AvailableOrderWindow } from '../interfaces/order-window';
import { Product } from '../models/product.model';

export enum StateChange {
  All,
  Inited,
  ActiveOrderWindow,
  CurrentOrderType,
  ActiveOrder,
  ActiveBWOrder,
  ActiveODOrder,
}

export class State {
  eventChange:
    | Omit<StateChange, 'inited' | 'currentOrderType'>
    | Array<Omit<StateChange, 'inited' | 'currentOrderType'>>;
  inited: boolean;
  activeOrderWindow: OrderWindow;
  currentOrderType: string;
  activeOrder: Order;
  activeBWOrder: Order;
  activeODOrder: Order;
}

@Injectable()
export class OrderService extends BaseService {
  orderWindows: OrderWindow[] = [];
  switchingOrderWindow = false;
  orders: Array<Order> = [];
  selectedOrder: Order = null;
  cartSelectedOrdersType = '';
  fetchingOrderWindows = false;

  id: any;
  isUpdatingTaxExempt = false;
  // autoshipOrderCheckouted
  // it should send autoship order id which is checkout  to update status
  autoshipOrderCheckouted = new Subject<number>();

  protected trackingCache: CacheModel;

  private initializedSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private currentOrderTypeSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);

  private stateSubject: BehaviorSubject<Omit<State, 'inited'>> =
    new BehaviorSubject<Omit<State, 'inited'>>({
      eventChange: StateChange.All,
      activeOrderWindow: null,
      currentOrderType: null,
      activeOrder: this.activeOrder,
      activeBWOrder: this.activeBWOrder,
      activeODOrder: this.activeODOrder,
    });

  initialized$ = this.initializedSubject.asObservable();
  currentOrderType$ = this.currentOrderTypeSubject.asObservable();
  state$ = this.stateSubject.asObservable();

  get state(): Omit<State, 'inited'> {
    return this.stateSubject?.value;
  }

  get initialized(): boolean {
    return this.initializedSubject?.value;
  }

  get currentOrderType(): string {
    return this.currentOrderTypeSubject?.value;
  }

  setState(state: Partial<State>): void {
    if (state.inited) {
      this.initializedSubject.next(state.inited);
    } else if (state.currentOrderType) {
      this.currentOrderTypeSubject.next(state.currentOrderType);
    } else {
      this.stateSubject.next({
        ...this.state,
        ...state,
      });
    }
  }

  constructor(
    private api: ApiOrderService,
    protected http: HttpClient,
    protected appSettings: AppSettings,
    protected toastr: ToastrService,
    private productService: ProductService,
    private authService: AuthService,
    private entityService: EntityService,
    private router: Router,
    private googleAnalytics: GoogleAnalytics,
    private localStorage: LocalStorageService,
    // private addressService: AddressService,
    private budgetService: BudgetService
  ) {
    super('/order', http, appSettings, toastr);
    // this.fetchData();

    this.trackingCache = new CacheModel();
    this.trackingCache.expiration = 3600;
  }

  // get order windows list and put into store
  getOrderWindows(fetchAllOrderWindows = false): void {
    // this.productService.onActiveOrderWindowChanged(0);

    if (!this.authService.canBuyingWindow) {
      this.getActiveOrders().subscribe(() => {
        this.setInited(true);
      });
    } else {
      this.fetchingOrderWindows = true;

      this.api.fetchOrderWindows(fetchAllOrderWindows, this.authService.isAdmin)
        .subscribe((orderWindows) => {
          this.fetchingOrderWindows = false;
          this.orderWindows = orderWindows;

          if (!this.state.activeOrderWindow) {
            this.setState({
              eventChange: StateChange.ActiveOrderWindow,
              activeOrderWindow: this.restoreActiveOrderWindow(orderWindows),
            });

            this.switchActiveOrderWindow(this.state.activeOrderWindow);
          }
        });
    }
  }

  public switchActiveOrderWindowById(id: number): OrderWindow {
    if (!id) {
      return;
    }

    const window = this.orderWindows.find((ow) => ow.id === id);
    if (!window) {
      // order window is not found
      this.router.navigateByUrl('/404', { skipLocationChange: true });
      return;
    }

    if (window.upcoming) {
      // upcoming windows are browse-only; never promote to active
      return window;
    }

    this.switchActiveOrderWindow(window);
    return window;
  }

  public switchActiveOrderWindow(orderWindow?: OrderWindow) {
    if (!orderWindow) {
      orderWindow = this.findLastActiveOrder();
    }

    if (orderWindow?.upcoming) {
      // upcoming windows are browse-only; never promote to active
      this.setInited(true);
      return;
    }

    if (orderWindow) {
      this.switchActiveOrderWindow$(orderWindow).subscribe(() => {});
    } else {
      this.setInited(true);
    }
  }

  public switchActiveOrderWindow$(orderWindow: OrderWindow): Observable<any> {
    if (orderWindow?.upcoming) {
      // upcoming windows are browse-only; never promote to active
      return of(null);
    }

    this.switchingOrderWindow = true;

    // get active orders
    return forkJoin([
      this.productService.onActiveOrderWindowChanged(orderWindow.id),
      this.getActiveOrders(orderWindow).pipe(
        tap(() => {
          this.setActiveOrderWindow(orderWindow);
          this.setInited(true);
          this.switchingOrderWindow = false;
        })
      ),
    ]);
  }

  public switchActiveOrder(order: Order): Observable<Order> {
    return this.getOrder(order.id).pipe(
      switchMap((resOrder) => {
        if (!resOrder || resOrder.is_active) {
          return of(resOrder);
        }
        return this.api.makeActiveOrder(resOrder.id).pipe(
          map((result) => {
            if (result) {
              const previousActiveOrders = this.findActiveOrdersByType(
                order.type
              );
              previousActiveOrders.forEach((o) => {
                o.is_active = false;
                this.updateOrderList(o);
              });

              const newActive = new Order(resOrder);
              newActive.is_active = true;

              this.updateOrderList(newActive);

              return newActive;
            }

            return resOrder;
          })
        );
      })
    );
  }

  private findActiveOrdersByType(type: string): Order[] {
    return this.orders.filter((o) => o.type === type && o.is_active);
  }

  private getActiveOrders(orderWindow?: OrderWindow): Observable<Order> {
    if (!this.authService.canSeeOrders) {
      this.setInited(true);
      return of(null);
    }

    return this.api.fetchActiveOrders(orderWindow).pipe(
      tap((orders) => {
        orders.forEach(
          (o) => (o.isWSLR = isWSLROrder(o, this.authService.user))
        );

        this.setOrders(orders);
      }),
      switchMap(() => this.getDefaultODOrder()),
      switchMap(() => this.getDefaultBWOrder())
    );
  }

  private getDefaultODOrder(): Observable<Order> {
    if (this.currentODOrders.length === 0) {
      return of(null);
    }

    const order = this.activeODOrder || this.currentODOrders[0];
    return this.switchActiveOrder(order);
  }

  private getDefaultBWOrder(): Observable<Order> {
    if (this.currentBWOrders.length === 0) {
      return of(null);
    }

    const order = this.activeBWOrder || this.currentBWOrders[0];
    return this.switchActiveOrder(order);
  }

  // returns last order window with active = true or undefined
  public findLastActiveOrder(): OrderWindow {
    return this.orderWindows.filter((i) => i.active === true).pop();
  }

  private findOrderById(id: number, forse = false): Observable<Order> {
    if (!forse) {
      const order = this.orders.find((o) => o.id === id);
      if (order && order.loaded) {
        return of(order);
      }
    }
    return this.api.fetchOrder(id);
  }

  private setOrderItem(order: Order) {
    if (!order) {
      return;
    }
    order.items.forEach((i) => {
      i.order_id = order.id;
      i.order_type = order.type;
    });
  }

  public updateOrderList(order: Order | number) {
    if (!order) {
      return;
    }

    if (typeof order === 'number') {
      // remove order from list
      this.setOrders(this.orders.filter((o) => o.id !== order));
      return;
    }

    const idx = this.orders.findIndex((o) => o.id === order.id);

    // check if order is WSLR
    order.isWSLR = isWSLROrder(order, this.authService.user);

    order.loaded = true;
    if (idx !== -1) {
      // update order in list
      this.setOrders(Object.assign([...this.orders], { [idx]: order }));
    } else {
      // add order to list
      this.setOrders([...this.orders, order]);
    }
  }

  private fetchOrderEntity(order: Order): Observable<Order> {
    if (order && order.entity_id > 0 && !order.entity) {
      return this.entityService.getEntityWithLocations(order.entity_id).pipe(
        map((entity) => {
          order.entity = entity;
          return order;
        })
      );
    }

    return of(order);
  }

  private fetchOrderDecisionPoints(order: Order): Observable<Order> {
    if (
      !order ||
      isWSLROrder(order, this.authService.user) ||
      !order.isDecisionPoint ||
      order.decisionPointData
    ) {
      return of(order);
    }

    return this.productService
      .searchDecisionPoints(order.attr_decision_point_name)
      .pipe(
        map((result) => {
          if (!isEmptyArray(result)) {
            order.decisionPointData = result[0];
          }
          return order;
        })
      );
  }

  private fetchAutoshipLocations(order: Order): Observable<Order> {
    if (
      !order ||
      !order.entity_id ||
      !order.isCustom ||
      !isEmptyArray(order.autoshipLocations)
    ) {
      return of(order);
    }
    return this.entityService.getAutoShipLocations(order.entity_id).pipe(
      map((locations) => {
        order.autoshipLocations = locations;
        return order;
      })
    );
  }

  private fetchBudgets(order: Order): Observable<Order> {
    if (!order || !order.canEdit) {
      return of(order);
    }
    return this.budgetService.getBudgetsByOrder(order);
  }

  public getOrder(id: number = null, forse = false): Observable<Order> {
    return this.findOrderById(id, forse).pipe(
      switchMap((order) => this.fetchOrderEntity(order)),
      switchMap((order) => this.fetchOrderDecisionPoints(order)),
      switchMap((order) => this.fetchBudgets(order)),
      switchMap((order) => this.fetchAutoshipLocations(order)),
      tap((resOrder) => {
        this.setOrderItem(resOrder);
        this.updateOrderList(resOrder);
      })
    );
  }

  getOrderHistory(
    filter: any = null,
    paging: string = '10-0',
    append = false
  ): Observable<{ orders: OrderHistory[]; count: number; type: string }> {
    return this.api
      .fetchOrderHistory(filter, paging)
      .pipe(map((result) => ({ ...result, type: filter.type })));
  }

  public getAvailableOrderWindows(): Observable<AvailableOrderWindow[]> {
    if (!this.authService.canBuyingWindow) {
      return of([]);
    }
    return this.api.getAvailableOrderWindows();
  }
  // getOrderHistoryCount(type: string): number {
  //     return this.api.getOrderHistoryCount(type);
  // }

  // Order level methods
  createOrder(type: string = null, orderOptions: any = {}): Observable<Order> {
    if (!type) {
      type = this.currentOrderType;
    }

    if (
      !this.authService.canOrder ||
      (type === OrderTypes.PREORDER_TYPE &&
        !(this.state.activeOrderWindow && this.state.activeOrderWindow.active))
    ) {
      console.log('Limited order is created.');

      let order = this.orders.find((o) => o.id === 0 && o.type === type);
      if (!order) {
        order = new Order({
          id: 0,
          is_active: true,
          items: [],
          items_count: 0,
          type,
          state: QUOTE_STATE,
          user: this.authService.user,
        });
        this.updateOrderList(order);
      }
      return of(order);
    }

    const windowID =
      type === OrderTypes.PREORDER_TYPE ? this.activeOrderWindowID : null;
    const body: any = {
      window_id: windowID,
      attr_cart_name: orderOptions.orderLabel
        ? orderOptions.orderLabel
        : this.getDefaultOrderName(type),
      attr_attention: this.getDefaultAttention(),
    };
    if (type === OrderTypes.CUSTOM_TYPE) {
      body.autoship = true;
    }

    if (orderOptions.entity_id) {
      body.entity_id = orderOptions.entity_id;
    }

    return this.api.createOrder(body).pipe(
      switchMap((order) => {
        if (!order) {
          return of(order);
        }
        return this.getOrder(order.id, true);
      }),
      tap((order) => {
        if (order) {
          if (
            type === OrderTypes.PREORDER_TYPE ||
            type === OrderTypes.ECOMMERCE_TYPE
          ) {
            // make previous order not active
            const active =
              type === OrderTypes.PREORDER_TYPE
                ? this.activeBWOrder
                : this.activeODOrder;
            if (active) {
              const previousActive = new Order(active);
              previousActive.is_active = false;
              this.updateOrderList(previousActive);
            }

            order.is_active = true;
            this.updateOrderList(order);
          }

          const message =
            'New ' +
            (order.type === OrderTypes.ECOMMERCE_TYPE ? 'Cart' : 'Order') +
            ' has been Created.';
          this.toastr.success(message);
        }
      })
    );
  }

  public createLimitedOrder(type: string): Order {
    return new Order({
      id: 0,
      is_active: true,
      items: [],
      items_count: 0,
      type,
      state: QUOTE_STATE,
      user: this.authService.user,
    });
  }
  updateCostCenter(order: Order, costCenter: CostCenter): Observable<Order> {
    if (!order || !costCenter) {
      return of(null);
    }
    if (costCenter) {
      costCenter.order_id = order.id;
    }

    return this.api.updateCostCenter(order.id, costCenter).pipe(
      map((result) => {
        if (result['errorCode']) {
          order.error = result as Error;
          return order;
        }

        if (result === true) {
          order.setPaymentMethod(costCenter);
          order.error = null;
          return order;
        }

        order.error = { errorCode: ErrorCodes.UNKNOWN_ERROR_CODE };
        return order;
      }),
      tap((retOrder) => {
        if (retOrder) {
          this.updateOrderList(retOrder);
        }
      })
    );
  }

  updateCreditCard(order: Order, creditCard: CreditCard): Observable<Order> {
    if (!order || !creditCard) {
      return of(null);
    }

    return this.api
      .setOrderAttribute(order.id, 'credit_card', {
        id: creditCard.id,
        label: creditCard.label,
      })
      .pipe(
        map((result) => {
          if (result === true) {
            order.setPaymentMethod(creditCard);
            order.error = null;
          } else {
            order.error = { errorCode: ErrorCodes.UNKNOWN_ERROR_CODE };
          }
          return order;
        })
      );
  }

  public removeCreditCardFromOrder(order: Order): Observable<Order> {
    return this.api.removeCreditCardFromOrder(order).pipe(
      map((result) => {
        if (result) {
          order.creditCard = null;
          order.error = null;
        } else {
          order.error = { errorCode: ErrorCodes.UNKNOWN_ERROR_CODE };
        }
        return order;
      })
    );
  }

  public setOrderAttributes(order: Order, attributes: any): Observable<Order> {
    return this.api.setOrderAttributes(order.id, attributes).pipe(
      switchMap((result) => {
        if (result) {
          // force to re-fetch order due to tax calculation
          return this.getOrder(order.id, true);
        }
        return of(order);
      })
    );
  }

  public updateCreditCardByOrderId(
    orderId: number,
    creditCard: CreditCard
  ): Observable<Order> {
    if (!orderId) {
      return of(null);
    }
    return this.getOrder(orderId).pipe(
      switchMap((order) => {
        return this.updateCreditCard(order, creditCard);
      })
    );
  }

  /**
   * update order entity, returns updated order
   * @param orderId - order id
   * @param entityId - entity id
   */
  updateEntity(order: Order, entityId: number): Observable<Order> {
    return this.api.updateOrderEntity(order.id, entityId).pipe(
      switchMap((retOrder) => {
        return this.fetchOrderWSLREntity(retOrder);
      })
    );
  }

  delete(orderID: number) {
    this.api.deleteOrder(orderID).subscribe((result) => {
      if (result) {
        this.updateOrderList(orderID);
        this.toastr.success('Your cart has been removed.');
      }
    });
  }

  printInvoice(orderID: number): Observable<any> {
    return this.api.getInvoice(orderID, '/print');
  }

  pdfInvoice(orderID: number): Observable<any> {
    return this.api.getInvoice(orderID, '/pdf');
  }

  emailInvoice(orderID: number, email: string): Observable<any> {
    return this.http
      .post<Response>(
        this.apiURL + '/' + orderID + '/invoice/email',
        { email: email },
        { headers: this.xsrfTokenHeader }
      )
      .pipe(
        map((response) => {
          return response.data;
        }),
        catchError(this.handleError('OrderService::emailInvoice', []))
      );
  }

  isInvoiceExistsForOrder(orderID: number): Observable<boolean> {
    return this.http
      .get<Response>(`${this.apiURL}/${orderID}/invoices/exist`)
      .pipe(
        map((response) => response.data?.invoicesExistForOrder),
        catchError(() => of(false))
      );
  }

  // Item level methods
  getOrderItem(order: Order, itemID: number) {
    this.api.getItem(order.id, itemID).subscribe((orderItem) => {});
  }

  createOrderItem(order: Order, item: OrderItem): Observable<OrderItem> {
    return this.api.createItem(order, item).pipe(
      tap((orderItem) => {
        if (orderItem) {
          this.googleAnalytics.addToShoppingCart(order, orderItem);
          this.updateOrderList(order);

          const product = orderItem.product;
          this.productService.updateProductList(product);
          // when order has been changed -  aggregated quantity should be cleared from cache
          this.productService.clearAggregateQuantitiesFromCache(product.id);

          if (product.inventoryType || product.has_inventory) {
            this.productService.productCache.delete(product.id);
            this.productService.putToCache(product);
          }
        }
      })
    );
  }

  updateOrderItem(order: Order, item: OrderItem): Observable<Order> {
    const orderItemID = item.id;
    return this.api.updateItem(order, orderItemID, item).pipe(
      map((retOrder) => {
        if (retOrder) {
          this.googleAnalytics.updateShoppingCart(retOrder, item);
          this.updateOrderList(retOrder);

          const orderItem = retOrder.items.find((i) => i.id === orderItemID);
          const product = orderItem.product;
          this.productService.updateProductList(product);
          this.productService.clearAggregateQuantitiesFromCache(product.id);

          if (product.inventoryType || product.has_inventory) {
            this.productService.productCache.delete(product.id);
            this.productService.putToCache(product);
          }

          return retOrder;
        }
        return null;
      })
    );
  }

  deleteOrderItem(order: Order, orderItem: OrderItem): Observable<Order> {
    if (!order || !orderItem) {
      return of(null);
    }
    const orderItemId = orderItem.id;
    const product = orderItem.product;
    const productId = product.id;

    return this.api.deleteItem(order, orderItemId).pipe(
      map((retOrder) => {
        if (retOrder) {
          this.productService.clearAggregateQuantitiesFromCache(productId);

          this.googleAnalytics.removeFromShoppingCart(retOrder, orderItem);
          this.updateOrderList(retOrder); // update order list

          if (product.inventoryType || product.has_inventory) {
            this.productService.productCache.delete(product.id);
          }

          return retOrder;
        }
        return null;
      })
    );
  }

  public updateOrderTaxExempt(
    order: Order,
    taxExempt: boolean
  ): Observable<Order> {
    this.isUpdatingTaxExempt = true;
    return this.setOrderAttributes(order, {
      tax_exempt: taxExempt.toString(),
    }).pipe(
      switchMap((result) => {
        if (result) {
          // re-fetch the order to get correct taxes
          return this.getOrder(order.id, true);
        }
        // failed update
        return of(order);
      }),
      tap(() => (this.isUpdatingTaxExempt = false))
    );
  }

  public performInventoryCheck(
    order: Order
  ): Observable<{ status: boolean; order: Order }> {
    return this.api.performInventoryCheck(order).pipe(
      map((result) => {
        if (!result) {
          return { status: false, order: order };
        }

        // if (!result.error) {
        //     // success user case
        //     return {status: true, order: order};
        // }

        if (!isEmptyArray(result.data?.products)) {
          // update product cache
          this.productService.updateProducts(result.data.products);

          result.data.products.forEach((product) => {
            // update product states
            const orderItem = order.items.find(
              (i) => i.product_id === product.id
            );
            if (orderItem) {
              orderItem.product = new Product(product);
            }
          });
        }

        if (!isEmptyArray(result.data?.warnings)) {
          const warnings = result.data.warnings.join('<br/>');
          this.toastr.warning(warnings, 'Inventory Check Failed', {
            enableHtml: true,
          });
          return { status: false, order: order };
        }

        if (result.error) {
          return { status: false, order: order };
        }

        return { status: true, order: order };
      })
    );
  }

  checkout(order: Order, updatePayment = false): Observable<Order> {
    if (!order) {
      return of(order);
    }

    this.googleAnalytics.purchase(order);

    return this.performInventoryCheck(order).pipe(
      switchMap((result) => {
        if (result.status === true) {
          // inventory check passed
          return this.api.checkout(order.id).pipe(
            map((orderCheckResult) => {
              if (
                typeof orderCheckResult === 'boolean' &&
                orderCheckResult === false
              ) {
                order.error = { errorCode: ErrorCodes.UNKNOWN_ERROR_CODE };
              }

              if (orderCheckResult['errorCode']) {
                order.error = orderCheckResult as Error;
              }

              return order;
            }),
            tap((retOrder) => {
              if (retOrder && !retOrder.error) {
                this.toastr.success('Order has been submitted');
                if (order.isCustom) {
                  this.autoshipOrderCheckouted.next(order.id);
                }
              }
            })
          );
        } else {
          result.order.error = { errorCode: ErrorCodes.UNKNOWN_ERROR_CODE };
          return of(result.order);
        }
      })
    );
  }

  setInited(val: boolean) {
    this.setState({ eventChange: StateChange.Inited, inited: val });
  }

  // async setActiveOrder(order: Order) {
  //     if (order) {
  //         if (order.type === ECOMMERCE_TYPE) {
  //             this.setActiveODOrder(order);
  //         } else {
  //             this.setActiveBWOrder(order);
  //         }
  //
  //         this.setActive(order.id).pipe(take(1)).subscribe(noop => {
  //             this.resetActive(order.id, order.type);
  //             this.setCurrentOrders();
  //             this.setCurrentActiveOrder(order);
  //         });
  //     }
  // }

  setSelectedOrder(order: Order) {
    this.selectedOrder = order;
  }

  get odCount() {
    return this.availableODOrders.length;
  }

  get bwCount() {
    return this.availableBuyingWindowOrders.length;
  }

  get branchCount() {
    return this.branchOrders.length;
  }

  get summaryCount() {
    return this.summaryOrders.length;
  }

  get autoshipCount() {
    return this.autoshipOrders.length;
  }

  get bwTotalCount() {
    return this.bwCount + this.branchCount + this.summaryCount;
  }

  public createAutoShipOrder(): Observable<Order> {
    if (
      !this.authService.canOrder ||
      this.authService.canCustomRequest === false
    ) {
      return of(null);
    }

    return this.createOrder(OrderTypes.CUSTOM_TYPE).pipe(
      tap((order: Order) => {
        if (order) {
          this.updateOrderList(order);
        }
      })
    );
  }

  public getApiUrl() {
    return this.apiURL;
  }

  /**
   * refresh tracking_numbers in order delivires
   * result is cached
   * @param order
   */
  getTrackingData(order: Order): Observable<Order> {
    return this.http
      .get<Response>(`${this.apiURL}/${order.id}/tracking-state`)
      .pipe(
        map((response) => {
          if (!isEmptyArray(response.data)) {
            return this.mapTracksToShipment(order, response.data);
          }
          return order;
        }),
        catchError(this.handleError('OrderService::tracking-state', null))
      );
  }

  /**
   * map track_numbers to related delivery items
   * @param order
   * @param trackData, contains tracking-numbers
   */
  private mapTracksToShipment(order: Order, trackData: any[]): Order {
    // TODO move to order model
    if (isEmptyArray(trackData) || isEmptyArray(order.items)) {
      return; // empty track number
    }

    const orderItems = order.items.map((i) => new OrderItem(i));
    let updatedTrackingData = false;
    orderItems.forEach((item) => {
      if (item.deliveries) {
        item.deliveries.forEach((delivery) => {
          const shipment = trackData.find(
            (data) => data.shipment_id === delivery.id
          );
          if (shipment) {
            delivery.tracking_number = shipment.tracking_number;
            updatedTrackingData = true;
          }
        });
      }
    });

    if (updatedTrackingData) {
      order.items = orderItems;
    }
    return order;
  }

  setActiveOrderWindow(orderWindow?: OrderWindow) {
    // this.activeOrderWindow = orderWindow;
    this.setState({
      eventChange: StateChange.ActiveOrderWindow,
      activeOrderWindow: orderWindow,
    });

    // save to local storage
    if (this.state.activeOrderWindow) {
      this.localStorage.setItem(
        ACTIVE_ORDER_WINDOW_ID,
        '' + this.state.activeOrderWindow.id
      );
    }
  }

  // for BW order user can't add/modify order items in case oder window is not defined or not acvtive
  canEditOrderItems(order: Order): boolean {
    if (order.type !== OrderTypes.PREORDER_TYPE) {
      return true;
    }

    return this.state.activeOrderWindow && this.state.activeOrderWindow.active;
  }

  public canPurchase(order: Order, orderItem: OrderItem): boolean {
    if (!order) {
      return false;
    }

    if (!this.canEditOrderItems(order)) {
      return false;
    }

    return orderItem.canPurchase;
  }

  setCurrentOrderType(type: string, emitAll = true) {
    // this.currentOrderType = type;
    // the rest values depends on currentOrderType
    // that's why it is set firstly
    this.setState({
      eventChange: StateChange.CurrentOrderType,
      currentOrderType: type,
    });

    this.setState({
      eventChange: emitAll
        ? StateChange.All
        : [
            StateChange.ActiveOrderWindow,
            StateChange.ActiveOrder,
            StateChange.ActiveBWOrder,
            StateChange.ActiveODOrder,
          ],
      activeOrder: this.activeOrder,
    });
  }

  get currentOrders(): Order[] {
    if (!this.currentOrderType) {
      return [];
    }

    return this.currentOrderType === OrderTypes.PREORDER_TYPE
      ? this.currentBWOrders
      : this.currentODOrders;
  }

  // reset all orders - should be called after logout
  reset(): void {
    // the rest values depends on currentOrderType
    // that's why it is set firstly
    this.setState({
      eventChange: StateChange.CurrentOrderType,
      currentOrderType: '',
    });

    this.setState({
      eventChange: [StateChange.ActiveOrderWindow, StateChange.ActiveOrder],
      activeOrderWindow: null,
      activeOrder: this.activeOrder,
    });

    // this.activeOrderWindow = null;
    this.orderWindows = [];
    this.setOrders([]);
    this.selectedOrder = null;
    // this.currentOrderType = '';

    this.api.clear();
  }

  // promocode

  applyPromoCode(order: Order, promoCode: string): Observable<Order> {
    return this.api.applyPromoCode(order, promoCode).pipe(
      map((retOrder) => {
        if (retOrder) {
          this.updateOrderList(retOrder);
          this.toastr.success('Promo Code has been applied');
        }
        return retOrder;
      })
    );
  }

  revokePromoCode(order: Order): Observable<Order> {
    return this.api.revokePromoCode(order).pipe(
      map((retOrder) => {
        if (retOrder) {
          this.updateOrderList(retOrder);
          this.toastr.success('Promo Code has been removed');
        }
        return retOrder;
      })
    );
  }

  private fetchOrderWSLREntity(order: Order): Observable<Order> {
    if (isWSLROrder(order, this.authService.user) && !order.entity) {
      return this.entityService.getEntityWithLocations(order.entity_id).pipe(
        map((entity) => {
          order.entity = entity;
          this.updateOrderList(order);
          return order;
        })
      );
    } else {
      return of(order);
    }
  }

  setOrders(orders: Order[]) {
    this.orders = orders;

    this.setState({
      eventChange: [
        StateChange.ActiveBWOrder,
        StateChange.ActiveODOrder,
        StateChange.ActiveOrder,
      ],
      activeBWOrder: this.activeBWOrder,
      activeODOrder: this.activeODOrder,
      activeOrder: this.activeOrder,
    });
  }

  get activeOrderWindowID(): number {
    return this.activeOrderWindow?.id || 0;
  }

  get activeOrderWindow(): OrderWindow {
    return this.state.activeOrderWindow;
  }

  get availableODOrders(): Order[] {
    return this.orders.filter(
      (o) => o.type === OrderTypes.ECOMMERCE_TYPE && o.state === QUOTE_STATE
    );
  }

  get currentODOrders(): Order[] {
    return this.availableODOrders.filter((o) =>
      this.authService.canEditOrder(o)
    );
  }

  get availableBuyingWindowOrders(): Order[] {
    return this.orders.filter((o) => o.type === OrderTypes.PREORDER_TYPE);
  }

  get buyingWindowOrders(): Order[] {
    return this.availableBuyingWindowOrders.filter((o) =>
      this.authService.canEditOrder(o)
    );
  }

  get autoshipOrders(): Order[] {
    return this.orders.filter((o) => o.type === OrderTypes.CUSTOM_TYPE);
  }

  get branchOrders(): Order[] {
    return this.orders.filter((o) => o.type === OrderTypes.BRANCH_TYPE);
  }

  get summaryOrders(): Order[] {
    return this.orders.filter((o) => o.type === OrderTypes.SUMMARY_TYPE);
  }

  get currentBWOrders(): Order[] {
    if (this.authService.isWholeSalerMultiple && this.branchOrders.length > 0) {
      return this.buyingWindowOrders.concat(this.branchOrders);
    } else {
      return this.buyingWindowOrders;
    }
  }

  public get activeODOrder(): Order {
    return this.currentODOrders.find((o) => o.is_active);
  }

  public get activeBWOrder(): Order {
    return this.currentBWOrders.find((o) => o.is_active);
  }

  public get activeOrder(): Order {
    // if (!this.currentOrderType) {
    //     return null;
    // }
    //

    return this.currentOrderType === OrderTypes.PREORDER_TYPE
      ? this.activeBWOrder
      : this.activeODOrder;
  }

  public getDefaultOrderName(type: string): string {
    const creationDate = dayjs().format('MMM-DD-YYYY');
    let orderType = '';
    if (type === OrderTypes.ECOMMERCE_TYPE) {
      orderType = 'On Demand';
    } else if (type === OrderTypes.PREORDER_TYPE) {
      // bw order
      orderType = this.state.activeOrderWindow
        ? this.state.activeOrderWindow.label
        : 'Buying Window';
    } else if (type === OrderTypes.CUSTOM_TYPE) {
      orderType = 'Custom Request';
    }

    return `${orderType} ${creationDate}`;
  }

  public getDefaultAttention(): string {
    if (!this.authService.user) {
      return '';
    }
    return `${this.authService.user.first_name} ${this.authService.user.last_name}`;
  }

  public buyingWindowChanged(): Observable<OrderWindow> {
    return this.state$.pipe(
      filter((e) =>
        [StateChange.ActiveOrderWindow, StateChange.All].some((s) =>
          [e.eventChange].flat().includes(s)
        )
      ),
      map((s) => s.activeOrderWindow)
    );
  }

  public activeOrderWindowChanged(): Observable<OrderWindow> {
    return this.state$.pipe(
      filter((e) =>
        [StateChange.ActiveOrderWindow, StateChange.All].some((s) =>
          [e.eventChange].flat().includes(s)
        )
      ),
      map((s) => s.activeOrderWindow)
    );
  }

  public activeOrderChanged(): Observable<Order> {
    return this.state$.pipe(
      filter((e) =>
        [StateChange.ActiveOrder, StateChange.All].some((s) =>
          [e.eventChange].flat().includes(s)
        )
      ),
      map((s) => s.activeOrder)
    );
  }

  public activeBWOrderChanged(): Observable<Order> {
    return this.state$.pipe(
      filter((e) =>
        [StateChange.ActiveBWOrder, StateChange.All].some((s) =>
          [e.eventChange].flat().includes(s)
        )
      ),
      map((s) => s.activeBWOrder)
    );
  }

  public activeODOrderChanged(): Observable<Order> {
    return this.state$.pipe(
      filter((e) =>
        [StateChange.ActiveODOrder, StateChange.All].some((s) =>
          [e.eventChange].flat().includes(s)
        )
      ),
      map((s) => s.activeODOrder)
    );
  }

  private restoreActiveOrderWindow(orderWindows: OrderWindow[]): OrderWindow {
    if (!this.state.activeOrderWindow) {
      // get from localstorage
      const activeOrderId = +this.localStorage.getItem(ACTIVE_ORDER_WINDOW_ID);
      const orderWindow = orderWindows.find((o) => o.id === activeOrderId); // make sure order window exists in the list
      if (orderWindow) {
        this.setState({
          eventChange: StateChange.ActiveOrderWindow,
          activeOrderWindow: orderWindow,
        });
      }
    }

    return this.state.activeOrderWindow;
  }

  public get currentOrderWindowId(): number {
    if (this.currentOrderType === OrderTypes.PREORDER_TYPE) {
      return this.activeOrderWindowID;
    }

    return 0;
  }

  getOrdersByType(type: string): Order[] {
    switch (type) {
      case OrderTypes.PREORDER_TYPE:
        return this.currentBWOrders;
      case OrderTypes.ECOMMERCE_TYPE:
        return this.currentODOrders;
      case OrderTypes.BRANCH_TYPE:
        return this.branchOrders;
      case OrderTypes.SUMMARY_TYPE:
        return this.summaryOrders;
      case OrderTypes.CUSTOM_TYPE:
        return this.autoshipOrders;
      default:
        return [];
    }
  }

  getOrderCountsByType(type: string): number {
    switch (type) {
      case OrderTypes.PREORDER_TYPE:
        return this.bwCount;
      case OrderTypes.ECOMMERCE_TYPE:
        return this.odCount;
      case OrderTypes.CUSTOM_TYPE:
        return this.autoshipCount;
      case OrderTypes.BRANCH_TYPE:
        return this.branchCount;
      case OrderTypes.SUMMARY_TYPE:
        return this.summaryCount;
      default:
        return 0;
    }
  }

  public get ordersByType(): {
    type: string;
    label: string;
    subLabel: string;
    orders: Order[];
  }[] {
    const orderList: {
      type: string;
      label: string;
      subLabel: string;
      orders: Order[];
    }[] = [];
    if (!this.initialized) {
      return orderList;
    }

    if (
      this.authService.canBuyingWindow &&
      !isEmptyArray(this.availableBuyingWindowOrders)
    ) {
      orderList.push({
        type: OrderTypes.PREORDER_TYPE,
        label: 'Buying Window Orders',
        subLabel: 'YOUR ORDERS',
        orders: this.currentBWOrders,
      });
    }

    if (!isEmptyArray(this.availableODOrders)) {
      orderList.push({
        type: OrderTypes.ECOMMERCE_TYPE,
        label: 'On Demand Orders',
        subLabel: 'YOUR ORDERS',
        orders: this.currentODOrders,
      });
    }

    if (
      this.authService.canCustomRequest &&
      !isEmptyArray(this.autoshipOrders)
    ) {
      orderList.push({
        type: OrderTypes.CUSTOM_TYPE,
        label: 'Custom Request Orders',
        subLabel: '',
        orders: this.autoshipOrders,
      });
    }

    if (
      this.authService.isWholeSalerMultiple &&
      !isEmptyArray(this.branchOrders)
    ) {
      orderList.push({
        type: OrderTypes.BRANCH_TYPE,
        label: 'Branch Orders',
        subLabel: '',
        orders: this.branchOrders,
      });
    }

    if (
      (this.authService.isWholeSaler ||
        this.authService.isWholeSalerMultiple) &&
      !isEmptyArray(this.summaryOrders)
    ) {
      orderList.push({
        type: OrderTypes.SUMMARY_TYPE,
        label: 'AB ORDERS',
        subLabel: '',
        orders: this.summaryOrders,
      });
    }

    if (!this.cartSelectedOrdersType && !isEmptyArray(orderList)) {
      this.cartSelectedOrdersType = orderList[0].type;
    }

    return orderList;
  }

  public get cartSelectedOrders(): {
    type: string;
    label: string;
    subLabel: string;
    orders: Order[];
  } {
    if (!this.cartSelectedOrdersType || isEmptyArray(this.ordersByType)) {
      return null;
    }

    return this.ordersByType.find(
      (i) => i.type === this.cartSelectedOrdersType
    );
  }

  public getActiveOrNewOrder(): Observable<Order> {
    if (this.activeOrder) {
      return of(this.activeOrder);
    }

    return this.createOrder();
  }

  public canDoQuickOrder(orderType: string): boolean {
    if (!orderType) {
      return false;
    }

    if (!this.authService.canOrder) {
      return false;
    }

    if (orderType === OrderTypes.PREORDER_TYPE) {
      // quick order can be available only for active bw
      return !!this.state.activeOrderWindow?.active;
    }

    return true;
  }

  public get orderWindowsForCalendar(): OrderWindow[] {
    if (isEmptyArray(this.orderWindows)) {
      return [];
    }

    return this.orderWindows.filter(i  =>  i.showInCalender);
  }
}
